import { useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IMPORT_TYPE_CSV, PENDING_IMPORT_ID } from '@constants/import-process';
import { EVENT_REPORT_ROUTE, PROCESS_IMPORT_ROUTE } from '@constants/routes';
import { CompanyClubContext } from '@contexts/company-club/company-club-context';
import { ImportFormData } from '@hooks/import/type';
import { useImportCsv } from '@hooks/import/use-import-csv';

/**
 * Handles the optimistic CSV import flow:
 * 1. Reads formData from route state
 * 2. Fires the POST mutation on mount
 * 3. Replaces the URL with the real importId once the mutation completes
 */
export const useOptimisticCsvImport = (importIdParam?: string, eventId?: string) => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasFiredMutation = useRef(false);
  const {
    clubState: { slug: clubSlug },
    companyState: { legacyId: companyLegacyId },
  } = useContext(CompanyClubContext);

  const formData = (location.state as { formData?: ImportFormData } | null)?.formData;
  const isPendingImport = importIdParam === PENDING_IMPORT_ID;
  const { data: importCsvData, mutate: importCsv, isPending: isSubmittingCsv, isError } = useImportCsv();

  const resolvedImportId = isPendingImport ? importCsvData?.importId : importIdParam;

  // Redirect to event report if pending import has no formData (e.g. page refresh or manual URL)
  useEffect(() => {
    if (isPendingImport && !formData && eventId) {
      const path = EVENT_REPORT_ROUTE.replace(':companyId', companyLegacyId)
        .replace(':clubSlug', clubSlug)
        .replace(':eventId', eventId);
      navigate(path, { replace: true });
    }
  }, [isPendingImport, formData, eventId, companyLegacyId, clubSlug, navigate]);

  useEffect(() => {
    if (isPendingImport && formData && !hasFiredMutation.current) {
      hasFiredMutation.current = true;
      importCsv({ importData: formData });
    }
  }, [isPendingImport, formData, importCsv]);

  useEffect(() => {
    if (isPendingImport && importCsvData?.importId && eventId) {
      const realPath = PROCESS_IMPORT_ROUTE.replace(':companyId', companyLegacyId)
        .replace(':clubSlug', clubSlug)
        .replace(':eventId', eventId)
        .replace(':importId', importCsvData.importId)
        .replace(':importType', IMPORT_TYPE_CSV);
      navigate(realPath, { replace: true });
    }
  }, [isPendingImport, importCsvData, eventId, companyLegacyId, clubSlug, navigate]);

  const isSubmittingPhase = isPendingImport && (isSubmittingCsv || !resolvedImportId);

  return { resolvedImportId, isSubmittingPhase, isSubmitError: isPendingImport && isError };
};
